import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import { cn } from '@april9au/react-common/utils';

import type { ImageField } from '@prismicio/client';

/**
 * An image that never pops in. A drop-in replacement for `<img>`: same element,
 * same classes, no wrapper — so swapping one in cannot move a layout.
 *
 * Three things go wrong with a bare `<img>` on a phone: the box does not exist
 * until the bytes arrive (everything below it jumps), the gap is the page
 * background (the screen looks broken rather than loading), and the pixels
 * appear in one frame (each photo flashes as it lands). `Media` fixes all
 * three in one place:
 *
 * - `width` and `height` are **required**, so the aspect box is reserved from
 *   the first paint. They describe the file; size the rendered image with
 *   `className` as usual.
 * - a `placeholder` colour is painted as the element's own background while it
 *   is still transparent, so the reveal is a fade between two similar surfaces
 *   rather than a flash. It is dropped once the image is up, so a transparent
 *   PNG does not sit on a tinted square forever.
 * - the image fades once, on load. A cached or already-decoded image is
 *   `complete` on the first layout pass and starts opaque, so going back to a
 *   screen does not replay the fade.
 *
 * The transition is a one-shot opacity fade, never a looping shimmer: a pulsing
 * placeholder repaints forever and costs battery on a high-refresh screen.
 */
export type MediaProps = Omit<React.ComponentPropsWithoutRef<'img'>, 'width' | 'height' | 'loading'> & {
  /** The file's intrinsic width in pixels. Required — it reserves the box. */
  width: number;
  /** The file's intrinsic height in pixels. */
  height: number;
  /**
   * The colour under the image while it loads. Any CSS colour; defaults to a
   * tint of the inherited text colour, which reads on both themes. Pass the
   * asset's dominant tone for a reveal nobody notices.
   */
  placeholder?: string;
  /**
   * Above the fold on this screen: eager, high fetch priority, decoded before
   * the fade rather than after. Roughly one image per screen should set it.
   */
  priority?: boolean;
};

export function Media({
  width,
  height,
  placeholder = 'color-mix(in oklab, currentColor 8%, transparent)',
  priority = false,
  className,
  style,
  onLoad,
  src,
  ...props
}: MediaProps) {
  const ref = useRef<HTMLImageElement>(null);
  const [loaded, setLoaded] = useState(false);

  // A layout effect, not `useState(() => …)`: the ref is only attached once the
  // element exists, and running before paint means an image that is already in
  // cache is opaque on its first frame instead of fading in a second time.
  useLayoutEffect(() => {
    setLoaded(ref.current?.complete === true);
  }, [src]);

  const handleLoad = useCallback(
    (event: React.SyntheticEvent<HTMLImageElement>) => {
      setLoaded(true);
      onLoad?.(event);
    },
    [onLoad],
  );

  return (
    <img
      ref={ref}
      src={src}
      width={width}
      height={height}
      loading={priority ? 'eager' : 'lazy'}
      decoding={priority ? 'sync' : 'async'}
      fetchPriority={priority ? 'high' : 'auto'}
      onLoad={handleLoad}
      className={cn('transition-opacity duration-500 ease-out motion-reduce:transition-none', className)}
      style={{ ...style, opacity: loaded ? undefined : 0, backgroundColor: loaded ? undefined : placeholder }}
      {...props}
    />
  );
}

/**
 * A Prismic `ImageField` as a `Media`.
 *
 * Prismic reports every asset's own dimensions, so the reserved box is exact
 * rather than guessed; an unsized asset falls back to 16:9, which is what the
 * chat's aspect wrappers assume anyway. An empty field renders nothing —
 * callers that want a visible gap draw it themselves.
 */
export function PrismicMedia({
  image,
  alt,
  ...props
}: Omit<MediaProps, 'src' | 'width' | 'height' | 'alt'> & {
  image: ImageField;
  /** Overrides the asset's own alt text. Pass `""` for a decorative image. */
  alt?: string;
}) {
  if (image.url === null) {
    return null;
  }

  const { width, height } = image.dimensions ?? { width: 1600, height: 900 };

  return <Media src={image.url} alt={alt ?? image.alt ?? ''} width={width} height={height} {...props} />;
}
